/**
 * Coalesces authoritative session reloads so concurrent score submissions share
 * one in-flight sync and burst finishes debounce into a single snapshot load.
 */

const AUTHORITATIVE_DEBOUNCE_MS = 150;
const WRITER_REALTIME_DEDUPE_MS = 2000;

export class SessionSyncCoordinator {
  constructor() {
    this.inFlightPromise = null;
    this.pendingAuthoritative = false;
    this.authoritativeInFlight = false;
    this.debounceTimer = null;
    this.pendingDebouncedOpts = null;
    this.debouncedResolvers = [];
    this.lastWriterAuthoritativeSyncAt = 0;
    this.pendingFollowUpOpts = null;
    this.onStatusChange = null;
  }

  /** @param {'idle'|'syncing'} status */
  _setStatus(status) {
    this.onStatusChange?.(status);
  }

  _mergeOpts(a, b) {
    return {
      ignoreSaveGuard: !!(a?.ignoreSaveGuard || b?.ignoreSaveGuard),
      forceApplyRemoteNextUp: !!(a?.forceApplyRemoteNextUp || b?.forceApplyRemoteNextUp),
      skipNextAssignments: !!(a?.skipNextAssignments || b?.skipNextAssignments),
      syncProfile: b?.syncProfile || a?.syncProfile || 'full',
      syncContext: b?.syncContext || a?.syncContext || null,
      useTargetedPostScore: !!(b?.useTargetedPostScore || a?.useTargetedPostScore),
      debounceMs: b?.debounceMs ?? a?.debounceMs ?? 0,
    };
  }

  /**
   * @param {(opts: object) => Promise<unknown>} runSync
   * @param {object} opts
   */
  requestSync(runSync, opts = {}) {
    const merged = this._mergeOpts(this.pendingDebouncedOpts, opts);

    if (merged.ignoreSaveGuard && merged.debounceMs > 0 && !this.inFlightPromise) {
      return this._scheduleDebounced(runSync, merged);
    }

    return this._execute(runSync, merged);
  }

  _scheduleDebounced(runSync, opts) {
    this.pendingDebouncedOpts = this._mergeOpts(this.pendingDebouncedOpts, opts);

    if (this.debounceTimer != null) {
      return new Promise((resolve, reject) => {
        this.debouncedResolvers.push({ resolve, reject });
      });
    }

    return new Promise((resolve, reject) => {
      this.debouncedResolvers.push({ resolve, reject });
      this.debounceTimer = setTimeout(() => {
        const resolvers = this.debouncedResolvers;
        const finalOpts = this.pendingDebouncedOpts || opts;
        this.debouncedResolvers = [];
        this.debounceTimer = null;
        this.pendingDebouncedOpts = null;
        finalOpts.debounceMs = 0;
        this._execute(runSync, finalOpts)
          .then((result) => resolvers.forEach((r) => r.resolve(result)))
          .catch((err) => resolvers.forEach((r) => r.reject(err)));
      }, opts.debounceMs || AUTHORITATIVE_DEBOUNCE_MS);
    });
  }

  _execute(runSync, opts) {
    if (this.inFlightPromise) {
      if (opts.ignoreSaveGuard && !this.authoritativeInFlight) {
        this.pendingAuthoritative = true;
        this.pendingFollowUpOpts = this._mergeOpts(this.pendingFollowUpOpts, opts);
      }
      return this.inFlightPromise;
    }

    this._setStatus('syncing');
    if (opts.ignoreSaveGuard) {
      this.lastWriterAuthoritativeSyncAt = Date.now();
      this.authoritativeInFlight = true;
    }

    const promise = Promise.resolve(runSync(opts))
      .finally(() => {
        this.inFlightPromise = null;
        this.authoritativeInFlight = false;
        this._setStatus('idle');
        if (this.pendingAuthoritative) {
          this.pendingAuthoritative = false;
          const followUp = this.pendingFollowUpOpts
            ? { ...this.pendingFollowUpOpts, debounceMs: 0 }
            : { ignoreSaveGuard: true, debounceMs: 0 };
          this.pendingFollowUpOpts = null;
          this.inFlightPromise = this._execute(runSync, followUp);
          return this.inFlightPromise;
        }
        return undefined;
      });

    this.inFlightPromise = promise;
    return promise;
  }

  /**
   * Writer host: skip redundant realtime session_games sync shortly after
   * an authoritative sync we initiated.
   */
  shouldSkipWriterRealtimeSync(isWriterHost) {
    if (!isWriterHost) return false;
    return Date.now() - this.lastWriterAuthoritativeSyncAt < WRITER_REALTIME_DEDUPE_MS;
  }

  dispose() {
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.debouncedResolvers = [];
    this.pendingDebouncedOpts = null;
    this.pendingFollowUpOpts = null;
  }
}

export { AUTHORITATIVE_DEBOUNCE_MS, WRITER_REALTIME_DEDUPE_MS };
